import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError, openSecureFile } from "../lib/api";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Label } from "./ui/label";
import { toast } from "sonner";
import { FileText, Search, Loader2, CheckCircle2, XCircle, Euro, Download, FileSpreadsheet, Trash2, Receipt, ChevronDown, ChevronRight } from "lucide-react";

const fmtEUR = (v) => new Intl.NumberFormat("it-IT", { style: "currency", currency: "EUR" }).format(v || 0);
const fmtD = (iso) => (iso ? new Date(iso).toLocaleDateString("it-IT") : "—");
const STATO_STYLE = {
  "Da autorizzare": "bg-amber-100 text-amber-800 border-amber-300",
  "Autorizzata": "bg-emerald-100 text-emerald-800 border-emerald-300",
  "Respinta": "bg-red-100 text-red-800 border-red-300",
  "Pagata": "bg-emerald-700 text-white border-emerald-800",
};

export function CrediusContabilita({ isAdmin }) {
  const navigate = useNavigate();
  const [notule, setNotule] = useState(null);
  const [avvocati, setAvvocati] = useState([]);
  const [filtri, setFiltri] = useState({
    q: "", avvocato_id: "", tipo: "", interna: "", stato: "",
    from_date: "", to_date: "", cliente: "",
  });
  const [busy, setBusy] = useState(false);
  // Iter122k131 · Filtri a tendina richiudibili (default: chiusi per ridurre l'ingombro,
  // soprattutto su mobile). Il conteggio dei filtri attivi appare accanto al titolo.
  const [filtriOpen, setFiltriOpen] = useState(false);
  const filtriAttivi = Object.values(filtri).filter((v) => v && String(v).trim() !== "").length;

  useEffect(() => {
    api.get("/credius/meta").then((r) => setAvvocati(r.data.avvocati || [])).catch(() => {});
  }, []);

  const load = () => {
    const params = new URLSearchParams();
    Object.entries(filtri).forEach(([k, v]) => { if (v) params.set(k, v); });
    api.get(`/credius/notule?${params}`).then((r) => setNotule(r.data)).catch(() => setNotule([]));
  };

  useEffect(() => {
    const t = setTimeout(load, 300);
    return () => clearTimeout(t);
  }, [filtri]); // eslint-disable-line

  const run = async (fn, ok) => {
    setBusy(true);
    try { await fn(); if (ok) toast.success(ok); load(); }
    catch (err) { toast.error(formatApiError(err.response?.data?.detail) || "Errore"); }
    finally { setBusy(false); }
  };

  const totali = (notule || []).reduce((acc, n) => {
    acc.count += 1;
    acc.totale += (n.totale_netto || 0);
    if (n.stato === "Da autorizzare") acc.pendenti += (n.totale_netto || 0);
    if (n.stato === "Autorizzata") acc.autorizzate += (n.totale_netto || 0);
    if (n.stato === "Pagata") acc.pagate += (n.totale_netto || 0);
    return acc;
  }, { count: 0, totale: 0, pendenti: 0, autorizzate: 0, pagate: 0 });

  return (
    <div data-testid="credius-contabilita" className="space-y-4">
      {/* Widgets riassuntivi */}
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <StatCard label="Notule totali" value={totali.count} isCount />
        <StatCard label="Da autorizzare" value={totali.pendenti} color="amber" />
        <StatCard label="Autorizzate" value={totali.autorizzate} color="emerald" />
        <StatCard label="Pagate" value={totali.pagate} color="slate" />
      </div>

      {/* Filtri di ricerca — richiudibili (Iter122k131) */}
      <div className="bg-white rounded-xl border-2 border-slate-200 p-3 space-y-3">
        <div className="flex items-center gap-2 flex-wrap">
          <button type="button" data-testid="notule-filtri-toggle"
            onClick={() => setFiltriOpen((v) => !v)}
            className="flex items-center gap-2 text-xs font-bold uppercase text-slate-600 hover:text-slate-900 transition-colors"
            aria-expanded={filtriOpen} aria-controls="notule-filtri-panel">
            {filtriOpen ? <ChevronDown className="w-4 h-4" /> : <ChevronRight className="w-4 h-4" />}
            <Search className="w-4 h-4" /> Ricerca & Filtri
            {filtriAttivi > 0 && (
              <span data-testid="notule-filtri-count"
                className="ml-1 text-[10px] font-bold bg-indigo-600 text-white rounded-full px-1.5 py-0.5">
                {filtriAttivi}
              </span>
            )}
          </button>
          {filtriAttivi > 0 && (
            <button type="button" data-testid="notule-filtri-reset"
              onClick={() => setFiltri({ q: "", avvocato_id: "", tipo: "", interna: "", stato: "", from_date: "", to_date: "", cliente: "" })}
              className="text-[10px] font-semibold text-red-700 hover:text-red-900 underline">
              Azzera
            </button>
          )}
          <Button size="sm" variant="outline" data-testid="notule-export-excel"
            onClick={() => {
              const params = new URLSearchParams();
              Object.entries(filtri).forEach(([k, v]) => { if (v) params.set(k, v); });
              openSecureFile(`/credius/export/notule-excel?${params}`);
            }}
            className="ml-auto border-emerald-300 text-emerald-800 hover:bg-emerald-50">
            <FileSpreadsheet className="w-4 h-4 mr-1" /> Export Excel
          </Button>
        </div>
        {filtriOpen && (
        <div id="notule-filtri-panel" className="grid sm:grid-cols-4 gap-3">
          <div>
            <Label className="text-[10px] uppercase text-slate-500">Testo</Label>
            <Input data-testid="notule-q" value={filtri.q}
              onChange={(e) => setFiltri({ ...filtri, q: e.target.value })}
              placeholder="Codice, pratica, avvocato..." className="h-9 mt-1" />
          </div>
          <div>
            <Label className="text-[10px] uppercase text-slate-500">Cliente</Label>
            <Input data-testid="notule-cliente" value={filtri.cliente}
              onChange={(e) => setFiltri({ ...filtri, cliente: e.target.value })}
              placeholder="Nome cliente" className="h-9 mt-1" />
          </div>
          <div>
            <Label className="text-[10px] uppercase text-slate-500">Avvocato</Label>
            <select data-testid="notule-avvocato" value={filtri.avvocato_id}
              onChange={(e) => setFiltri({ ...filtri, avvocato_id: e.target.value })}
              className="h-9 w-full border-2 border-slate-300 rounded-lg px-2 bg-white mt-1 text-sm">
              <option value="">Tutti</option>
              {avvocati.map((a) => <option key={a.id} value={a.id}>{a.ragione_sociale}</option>)}
            </select>
          </div>
          <div>
            <Label className="text-[10px] uppercase text-slate-500">Tipo pratica</Label>
            <select data-testid="notule-tipo" value={filtri.tipo}
              onChange={(e) => setFiltri({ ...filtri, tipo: e.target.value })}
              className="h-9 w-full border-2 border-slate-300 rounded-lg px-2 bg-white mt-1 text-sm">
              <option value="">Tutti</option>
              <option value="stragiudiziale">Stragiudiziale</option>
              <option value="giudiziale">Giudiziale</option>
              <option value="sinistro">Sinistro</option>
            </select>
          </div>
          <div>
            <Label className="text-[10px] uppercase text-slate-500">Interna/Esterna</Label>
            <select data-testid="notule-interna" value={filtri.interna}
              onChange={(e) => setFiltri({ ...filtri, interna: e.target.value })}
              className="h-9 w-full border-2 border-slate-300 rounded-lg px-2 bg-white mt-1 text-sm">
              <option value="">Tutte</option>
              <option value="interna">Interna</option>
              <option value="esterna">Esterna</option>
            </select>
          </div>
          <div>
            <Label className="text-[10px] uppercase text-slate-500">Stato</Label>
            <select data-testid="notule-stato" value={filtri.stato}
              onChange={(e) => setFiltri({ ...filtri, stato: e.target.value })}
              className="h-9 w-full border-2 border-slate-300 rounded-lg px-2 bg-white mt-1 text-sm">
              <option value="">Tutti</option>
              <option>Da autorizzare</option>
              <option>Autorizzata</option>
              <option>Respinta</option>
              <option>Pagata</option>
            </select>
          </div>
          <div>
            <Label className="text-[10px] uppercase text-slate-500">Dal</Label>
            <Input type="date" data-testid="notule-from" value={filtri.from_date}
              onChange={(e) => setFiltri({ ...filtri, from_date: e.target.value })}
              className="h-9 mt-1" />
          </div>
          <div>
            <Label className="text-[10px] uppercase text-slate-500">Al</Label>
            <Input type="date" data-testid="notule-to" value={filtri.to_date}
              onChange={(e) => setFiltri({ ...filtri, to_date: e.target.value })}
              className="h-9 mt-1" />
          </div>
        </div>
        )}
      </div>

      {/* Elenco notule */}
      {notule === null ? (
        <div className="flex justify-center py-12"><Loader2 className="w-7 h-7 animate-spin text-indigo-800" /></div>
      ) : notule.length === 0 ? (
        <div data-testid="notule-empty" className="text-center py-12 text-slate-500">
          <FileText className="w-10 h-10 mx-auto mb-2 text-slate-300" />
          Nessuna notula pro-forma presente. Verranno mostrate qui quando gli avvocati le emetteranno dalle rispettive pratiche.
        </div>
      ) : (
        <div className="space-y-2">
          {notule.map((n) => (
            <div key={n.id} data-testid={`notula-row-${n.id}`}
              className="bg-white rounded-xl border-2 border-slate-200 p-3 space-y-2">
              <div className="flex flex-wrap items-center gap-2">
                <span className={`text-[10px] font-bold px-2 py-0.5 rounded-full border ${STATO_STYLE[n.stato] || "bg-slate-100"}`}>{n.stato}</span>
                <span className="font-mono text-xs text-slate-600">{n.codice_documento}</span>
                <button type="button" onClick={() => navigate(`/credius/${n.pratica_id}`)}
                  className="inline-flex items-center gap-1 font-bold text-indigo-800 hover:underline">
                  <FileText className="w-4 h-4" /> {n.pratica_numero}
                </button>
                <PartiInline clienti={n.pratica_clienti} controparti={n.pratica_controparti} />
                <span className="text-xs text-slate-500">{n.pratica_tipo} · {n.pratica_interna ? "Interna" : "Esterna"}</span>
                <span className="text-xs text-slate-500 ml-auto">{fmtD(n.created_at)}</span>
              </div>
              <div className="flex flex-wrap items-center gap-2 text-sm">
                <span className="text-slate-600">Avvocato: <b>{n.avvocato_nome}</b></span>
                <span className="text-slate-500">· Imponibile: {fmtEUR(n.imponibile)}</span>
                <span className="text-slate-500">· IVA: {fmtEUR(n.iva)}</span>
                <span className="ml-auto font-bold text-slate-900">Netto: {fmtEUR(n.totale_netto)}</span>
              </div>
              <div className="actions-row flex flex-wrap gap-2">
                <Button size="sm" variant="outline" data-testid={`notula-pdf-${n.id}`}
                  onClick={() => openSecureFile(`/credius/notule/${n.id}/pdf`)}
                  className="border-indigo-300 text-indigo-800 hover:bg-indigo-50">
                  <Download className="w-4 h-4 mr-1" /> PDF Notula
                </Button>
                {n.fattura_file_id && (
                  <Button size="sm" variant="outline" data-testid={`notula-fattura-${n.id}`}
                    onClick={() => openSecureFile(`/files/${n.fattura_file_id}`)}
                    className="border-emerald-300 text-emerald-800 hover:bg-emerald-50"
                    title={n.fattura_original_filename || "Fattura elettronica allegata"}>
                    <Receipt className="w-4 h-4 mr-1" /> Fattura{n.fattura_numero ? ` n. ${n.fattura_numero}` : ""}
                  </Button>
                )}
                {isAdmin && n.stato === "Da autorizzare" && (
                  <>
                    <Button size="sm" data-testid={`notula-valida-${n.id}`} disabled={busy}
                      onClick={() => run(() => api.post(`/credius/notule/${n.id}/valida`), "Notula validata")}
                      className="bg-emerald-700 hover:bg-emerald-800">
                      <CheckCircle2 className="w-4 h-4 mr-1" /> Valida
                    </Button>
                    <Button size="sm" variant="outline" data-testid={`notula-respingi-${n.id}`} disabled={busy}
                      onClick={() => {
                        const motivo = window.prompt("Motivo del rigetto (min 4 caratteri):");
                        if (motivo && motivo.trim().length >= 4) {
                          run(() => api.post(`/credius/notule/${n.id}/respingi`, { motivo: motivo.trim() }), "Notula respinta");
                        }
                      }}
                      className="border-red-300 text-red-700 hover:bg-red-50">
                      <XCircle className="w-4 h-4 mr-1" /> Respingi
                    </Button>
                  </>
                )}
                {n.stato === "Respinta" && n.respinta_motivo && (
                  <span className="text-xs text-red-700 italic">Motivo: {n.respinta_motivo}</span>
                )}
                {n.stato === "Pagata" && (
                  <span className="text-xs text-slate-500 italic">Pagata il {fmtD(n.pagata_at)}</span>
                )}
                {isAdmin && (
                  <Button size="sm" variant="outline" data-testid={`notula-elimina-${n.id}`} disabled={busy}
                    onClick={() => {
                      if (!window.confirm(`Eliminare la notula ${n.codice_documento}?\n\nLa cancellazione è logica (soft-delete): potrà essere recuperata dal DB in caso di errore.`)) return;
                      run(() => api.delete(`/credius/notule/${n.id}`), "Notula eliminata");
                    }}
                    title="Elimina notula (soft-delete)"
                    className="ml-auto border-red-300 text-red-700 hover:bg-red-50">
                    <Trash2 className="w-4 h-4" />
                  </Button>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

function StatCard({ label, value, color = "indigo", isCount = false }) {
  const bg = { amber: "bg-amber-50 border-amber-300", emerald: "bg-emerald-50 border-emerald-300",
                slate: "bg-slate-50 border-slate-300", indigo: "bg-indigo-50 border-indigo-300" }[color] || "bg-white";
  const text = { amber: "text-amber-800", emerald: "text-emerald-800",
                  slate: "text-slate-700", indigo: "text-indigo-900" }[color] || "text-slate-800";
  return (
    <div className={`rounded-xl border-2 p-3 ${bg}`}>
      <div className="text-xs font-bold uppercase text-slate-500">{label}</div>
      <div className={`text-lg font-bold ${text}`}>{isCount ? value : fmtEUR(value)}</div>
    </div>
  );
}


/*
  Iter122k128 · Mostra i nomi delle parti (cliente/i + controparte/i) affianco al numero
  pratica nella preview delle notule. Se ci sono più di 3 controparti, ne visualizza solo 3
  e aggiunge un indicatore "+N".
*/
export function PartiInline({ clienti = [], controparti = [] }) {
  const cliNames = (clienti || []).map((c) => (c?.nome || "").trim()).filter(Boolean);
  const ctrpNames = (controparti || []).map((c) => (c?.nome || "").trim()).filter(Boolean);
  if (cliNames.length === 0 && ctrpNames.length === 0) return null;
  const ctrpVisible = ctrpNames.slice(0, 3);
  const ctrpExtra = Math.max(0, ctrpNames.length - 3);
  return (
    <span data-testid="notula-parti-inline" className="inline-flex flex-wrap items-center gap-1 text-xs">
      {cliNames.length > 0 && (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-50 border border-emerald-200 text-emerald-800">
          <span className="text-[9px] font-bold uppercase">Cli:</span>
          <span className="font-semibold truncate max-w-[220px]" title={cliNames.join(", ")}>
            {cliNames.join(", ")}
          </span>
        </span>
      )}
      {ctrpVisible.length > 0 && (
        <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-red-50 border border-red-200 text-red-800">
          <span className="text-[9px] font-bold uppercase">Contr:</span>
          <span className="font-semibold truncate max-w-[240px]" title={ctrpNames.join(", ")}>
            {ctrpVisible.join(", ")}
          </span>
          {ctrpExtra > 0 && (
            <span data-testid="notula-parti-contr-extra"
              className="text-[9px] font-bold bg-red-200 text-red-800 rounded-full px-1.5 py-0.5"
              title={ctrpNames.slice(3).join(", ")}>
              +{ctrpExtra}
            </span>
          )}
        </span>
      )}
    </span>
  );
}
